import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { logger } from 'src/lib/logger';
import api, { shouldRetryQuery } from 'src/lib/api/session';
import { Device } from '../lib/api/types';

export const deviceKeys = {
  all: ['devices'] as const,
  lists: () => [...deviceKeys.all, 'list'] as const,
  detail: (id: string) => [...deviceKeys.all, 'detail', id] as const,
};

interface DeviceInfo {
  user_code: string;
  client_id: string;
  scope: string;
  expires_at: string;
  authorized: boolean;
}

interface DeviceInfoResponse {
  success: boolean;
  device_info?: DeviceInfo;
  error?: string;
  error_description?: string;
}

interface AuthorizeDeviceRequest {
  user_code: string;
  device_name?: string;
}

interface AuthorizeDeviceResponse {
  success: boolean;
  device_id?: string;
  error?: string;
  error_description?: string;
}

export const useDeviceInfo = () => {
  return useMutation({
    mutationFn: async (userCode: string): Promise<DeviceInfo> => {
      const response = await api.get<DeviceInfoResponse>(`/v1/oauth2/device/info/${userCode}`);

      if (!response.data.success || !response.data.device_info) {
        throw new Error(response.data.error_description || 'Invalid device code');
      }

      return response.data.device_info;
    },
  });
};

export const useAuthorizeDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AuthorizeDeviceRequest): Promise<{ device_id?: string }> => {
      const response = await api.post<AuthorizeDeviceResponse>('/v1/oauth2/device/authorize', request);

      if (!response.data.success) {
        throw new Error(response.data.error_description || 'Authorization failed');
      }

      return { device_id: response.data.device_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() }).catch((error) => logger.error('[useAuthorizeDevice] Failed to invalidate device lists:', error));
    },
  });
};

export const useDevice = (
  deviceId: string,
  options?: UseQueryOptions<Device, Error, Device, ReturnType<typeof deviceKeys.detail>>,
) => {

  return useQuery({
    queryKey: deviceKeys.detail(deviceId),
    queryFn: async (): Promise<Device> => {
      const response = await api.get<Device>(`/v1/user/devices/${deviceId}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: shouldRetryQuery,
    ...options,
  });
};

export const useDevices = (
  options?: UseQueryOptions<Device[], Error, Device[], ReturnType<typeof deviceKeys.lists>>,
) => {

  return useQuery({
    queryKey: deviceKeys.lists(),
    queryFn: async (): Promise<Device[]> => {
      const response = await api.get<Device[]>('/v1/user/devices');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: shouldRetryQuery,
    ...options,
  });
};

export interface ProvisionedDevice {
  deviceId: string;
  name: string;
  clientId: string;
  clientSecret: string;
}

export const useProvisionDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string): Promise<ProvisionedDevice> => {
      const { data: device } = await api.post<{ id: string; name: string }>('/v1/user/devices', { name });
      try {
        const { data: client } = await api.post<{ client_id: string; client_secret: string }>('/v1/oauth2/clients', {
          name: `${name} publisher`,
          device_scope: [device.id],
          scopes: ['sfu:publish'],
        });
        return { deviceId: device.id, name: device.name, clientId: client.client_id, clientSecret: client.client_secret };
      } catch (error) {
        await api.delete(`/v1/user/devices/${device.id}`).catch(() => {});
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

interface DeviceStreamsResponse {
  live_device_ids: string[];
}

export const useDeviceStreams = () => {

  return useQuery({
    queryKey: [...deviceKeys.all, 'streams'] as const,
    queryFn: async (): Promise<DeviceStreamsResponse> => {
      const response = await api.get<DeviceStreamsResponse>('/v1/user/devices/streams');
      return response.data;
    },
    refetchInterval: 10_000,
    retry: false,
  });
};

type UpdateDeviceNameValues = {
  deviceId: string;
  deviceName: string;
};

export const useUpdateDeviceName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: UpdateDeviceNameValues): Promise<Device> => {
      const response = await api.patch<Device>(`/v1/user/devices/${values.deviceId}`, {
        device_name: values.deviceName,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() }).catch((error) => logger.error('[useUpdateDeviceName] Failed to invalidate device lists:', error));
      queryClient
        .invalidateQueries({ queryKey: deviceKeys.detail(data.id) })
        .catch((error) => logger.error('[useUpdateDeviceName] Failed to invalidate device detail:', error));
    },
  });
};

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceId: string): Promise<void> => {
      await api.delete(`/v1/user/devices/${deviceId}`);
    },
    onSuccess: (_, deviceId) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() }).catch((error) => logger.error('[useDeleteDevice] Failed to invalidate device lists:', error));
      queryClient.removeQueries({ queryKey: deviceKeys.detail(deviceId) });
    },
  });
};


